<template>
    <div>
        <tsoft-table
            name="news"
            :key="componentRefreshKey"
            :api="tableApiUrl"
            :items.sync="items"
            :isNeedSearch="true"
            :additionalGetParameter="additionalGetParameter"
            :isNeedCreate="isNeedCreate && isAdmin"
            :typeOfTableFilter="typeOfTableFilter"
            :onDataEmptyMessage="'Новости по заданному фильтру отсутствуют. Попробуйте снова.'"
            @show="showItem"
            @edit="createOrEditItemModal"
            @delete="deleteItem"
            @publish="publishNews"
            @schedule="scheduleNews"
            @archive="archiveNews"
        />

        <b-overlay
            :show="dataOverlay"
            spinner-variant="success"
            no-wrap
        ></b-overlay>

        <b-sidebar
            v-if="isSidebarOpen"
            v-model="isSidebarOpen"
            id="sidebar-right-create-or-edit-news"
            right
            backdrop
            shadow
            width="65em"
            backdrop-variant="dark"
            ref="editItem"
            no-close-on-backdrop
            @change="closeSidePanelCallback()"
        >
            <template #title>
                <div>
                    <strong id="sidebar-right___title__" v-html="modalTitle()"></strong>
                </div>
            </template>
            <div class="d-block">
                <NewsProfile 
                    :news="editedItem" 
                    @closed="isSidebarOpen=false"
                    @updated="profileUpdated"
                />
            </div>
        </b-sidebar>
        
        <!-- Сайдбар для просмотра новости -->
        <NewsDetailSidebar
            v-model="isSidebarOpenDetail"
            :news="detailedItem"
            @close="onDetailSidebarClose"
        />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import {API_NEWS} from "@/constants";
    import NewsProfile from "@/components/news/NewsProfile";
    import NewsDetailSidebar from "@/components/news/NewsDetailSidebar";

    import {
        defaultDataItems,
        actionShowItem,
        actionCreateOrUpdateItem,
        actionDeleteItem
    } from '@/mixins';

    const initialEditedItem = () => ({
        id: null,
        title: '',
        short_description: '',
        content: '',
        image: null,
        published_at: null,
        scheduled_at: null,
        status: 'draft',
        category: 'internal_employees',
        creator_user_id: null,
        creator: {}
    })

    export default {
        components: {
            NewsProfile,
            NewsDetailSidebar
        },    
        mixins: [
            defaultDataItems,
            actionShowItem,
            actionDeleteItem,
            actionCreateOrUpdateItem
        ],
        props: {
            isNeedCreate: { type: Boolean, required: false, default: true },        // If table needs create new element button
            additionalGetParameter: { type: String, required: false, default: '' }, // If we want to add something, for example, &category=b2b
            typeOfTableFilter: { type: String, required: false, default: 'news' },      // Type of filter for output: news
        },
        data() {
            return {
                loading: false,
                tableApiUrl: API_NEWS,
                componentRefreshKey: 0,
                dataOverlay: false
            }
        },
        computed: {
            ...mapGetters('auth', ['currentUser']),

            isAdmin() {
                return this.currentUser.role.includes('administrator');
            }
        },
        watch: {
        },
        mounted() {
            this.editedItem = initialEditedItem();
            this.initialAssign();
        },
        methods: {
            initialAssign() {
                // Инициализация данных
            },
            // calls from mixin before sidebaropened
            beforeSidebarOpenedCallback() {
                this.editedItem = initialEditedItem();
                this.initialAssign();
            },
            // calls from mixin on item save
            onCreatedOrUpdatedCallback() {
                this.isSidebarOpen = false
            },
            profileUpdated(data) {
                // If editIndex > -1 → update existing row; else → prepend new row
                if (this.editIndex > -1 && this.items && this.items.data && this.items.data[this.editIndex]) {
                    Object.assign(this.items.data[this.editIndex], data);
                } else if (this.items && this.items.data) {
                    this.items.data.unshift(data);
                }

                // Close sidebar and reset state
                this.onCreatedOrUpdatedCallback();
                this.editIndex = -1;
                this.validationErrors = '';
                this.makeToast('success');
            },
            onDeletedCallback() {
                this.componentRefreshKey++
                this.updateParentData()
            },
            modalTitle() {
                let title = 'Добавление новости'
                if (this.editIndex > -1) {
                    title = 'Редактирование новости #' + this.editedItem.id
                }

                return title
            },
            updateParentData() {
                this.$emit("updateParentData")
            },
            
            setFilteringApiParameter(additionalParameter) {
                // Обновляем дополнительные параметры для API
                this.additionalGetParameter = additionalParameter;
                
                // Обновляем таблицу
                this.componentRefreshKey++;
            },
            
            // Обработчики для действий с новостями
            async publishNews(index) {
                try {
                    this.dataOverlay = true;
                    const news = this.items.data[index];
                    const response = await api.call('post', `${API_NEWS}/${news.id}/publish`);
                    
                    if (response.data.success) {
                        // Обновляем данные в таблице
                        Object.assign(this.items.data[index], response.data.data);
                        this.makeToast('success', 'Новость успешно опубликована');
                    }
                } catch (error) {
                    console.error('Ошибка при публикации новости:', error);
                    this.makeToast('error', 'Ошибка при публикации новости');
                } finally {
                    this.dataOverlay = false;
                }
            },
            
            async scheduleNews(index) {
                try {
                    this.dataOverlay = true;
                    const news = this.items.data[index];
                    
                    // Показываем модальное окно для выбора даты
                    this.$bvModal.msgBoxConfirm('Выберите дату и время для публикации:', {
                        title: 'Запланировать публикацию',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'success',
                        okTitle: 'Запланировать',
                        cancelTitle: 'Отмена',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true,
                        inputType: 'datetime-local',
                        inputValue: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16), // Завтра в это же время
                        inputValidator: (value) => {
                            if (!value) return 'Пожалуйста, выберите дату';
                            if (new Date(value) <= new Date()) return 'Дата должна быть в будущем';
                            return true;
                        }
                    }).then(async (value) => {
                        if (value) {
                            const scheduledAt = new Date(value).toISOString();
                            const response = await api.call('post', `${API_NEWS}/${news.id}/schedule`, {
                                scheduled_at: scheduledAt
                            });
                            
                            if (response.data.success) {
                                Object.assign(this.items.data[index], response.data.data);
                                this.makeToast('success', 'Новость запланирована на публикацию');
                            }
                        }
                    });
                } catch (error) {
                    console.error('Ошибка при планировании новости:', error);
                    this.makeToast('error', 'Ошибка при планировании новости');
                } finally {
                    this.dataOverlay = false;
                }
            },
            
            async archiveNews(index) {
                try {
                    this.dataOverlay = true;
                    const news = this.items.data[index];
                    const response = await api.call('post', `${API_NEWS}/${news.id}/archive`);
                    
                    if (response.data.success) {
                        // Обновляем данные в таблице
                        Object.assign(this.items.data[index], response.data.data);
                        this.makeToast('success', 'Новость перемещена в архив');
                    }
                } catch (error) {
                    console.error('Ошибка при архивировании новости:', error);
                    this.makeToast('error', 'Ошибка при архивировании новости');
                } finally {
                    this.dataOverlay = false;
                }
            },
            
            onDetailSidebarClose() {
                this.detailedItem = {
                    creator: {},
                    type: {},
                    sales_manager: {},
                    source: {},
                    company: {},
                    companies: [],
                    company_employee: {},
                    client: {},
                    status: {},
                    order: {},
                    service: {},
                    service_for_partner: {},
                    services_list: {},
                    selected_services: [],
                    user: {},
                    close_reason: {},
                    documents: {},
                    teo_manager: {},
                    to_manager: {},
                    optional_sales_manager: {},
                    finance_substatuses: [],
                    passport_checked: {},
                    driver_card_checked: {},
                    passport_file_photo: {},
                    passport_file_address: {},
                    driver_card_file_face: {},
                    driver_card_file_back: {},
                    truck_type: {},
                    truck_body_type: {},
                    checked: {},
                    ctc_file_face: {},
                    ctc_file_back: {},
                    ptc_file_face: {},
                    ptc_file_back: {},
                    rent_file: {},
                    order_substatuses: {},
                    contacts: {
                        lead_fio: "",
                        lead_email: "",
                        lead_phone: "",
                    },
                };
            },
            
            // Переопределяем метод showItem для использования нашего компонента
            showItem(index, tabSelector = '', name) {
                let id = this.items.data[index].id;
                
                api.call("get", `${this.tableApiUrl}/${id}`).then(({data}) => {
                    this.detailedItem = data;
                    this.isSidebarOpenDetail = true;
                    this.detailedItemIndex = index;
                    // If this element exist in table lets update
                    if (typeof this.items !== 'undefined' && this.items.data[index]) {
                        this.items.data[index] = Object.assign(this.items.data[index], data);
                    }
                });
            }
        }
    }
</script>
